import React, { useEffect, useRef, useState } from 'react';
import { useTranslator, useUrlLocalizer } from 'tradera-lang/translate';
import { useDispatch, useSelector } from 'react-redux';
import { useShowMore } from 'tradera-hooks/use-show-more';
import useIsLoggedIn from 'tradera-hooks/use-is-loggedin';
import { selectMemberId } from 'tradera-state/member/selectors';
import uiActions from 'tradera-state/ui/actions';
import dayjs from 'dayjs';
import { Button } from 'tradera-components/atoms/button/button';
import ALink from 'tradera-components/alink/alink';
import { ProfileShareButton } from '../profile-share-button';
import { followSeller, unfollowSeller } from './api';
import { CombinedShippingBadge } from 'tradera-components/combined-shipping-badge/combined-shipping-badge';
import RatingStars from 'tradera-components/rating-stars/rating-stars';
import { formatNumberWithSeparators } from 'tradera-utils/format';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import StarIcon from '@tradera/blueprint/theme/icons2/star.svg?jsx';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import styles from './profile-details.module.scss';

const trackFollowSellerClick = (isFollowing, profileMemberId) => {
    GtmService.trackAction(
        'User Profile',
        isFollowing ? 'Unfavourite' : 'Favourite',
        profileMemberId
    );
};

const ProfileDetails = ({ profileData }) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const isLoggedIn = useIsLoggedIn();
    const loggedInMemberId = useSelector(selectMemberId);
    const {
        alias,
        city,
        combinedShippingDays,
        existsInFavouriteSellers,
        detailedSellerRatingAverage,
        hasShop,
        heroImages,
        intro,
        memberSince,
        profileMemberId,
        totalRating
    } = profileData;

    const [isFavoriteSeller, setIsFavoriteSeller] = useState(
        existsInFavouriteSellers
    );

    const hasImage = !!heroImages?.max;

    const detailsRef = useRef();
    const detailsContainerRef = useRef();

    const [maxTextHeight, setMaxTextHeight] = useState(hasImage ? null : 84);

    useEffect(() => {
        if (hasImage) {
            const containerHeight = detailsContainerRef.current.getBoundingClientRect()
                .height;
            const detailsHeight = detailsRef.current.getBoundingClientRect()
                .height;
            const heightLeft = containerHeight - detailsHeight;
            const roundedToLineHeight = heightLeft - (heightLeft % 21);
            setMaxTextHeight(roundedToLineHeight);
        } else {
            setMaxTextHeight(84);
        }
    }, [hasImage, profileMemberId]);

    const handleFollowSeller = async () => {
        if (isLoggedIn) {
            try {
                trackFollowSellerClick(isFavoriteSeller, profileMemberId);
                if (isFavoriteSeller) {
                    setIsFavoriteSeller(false);
                    await unfollowSeller(profileMemberId);
                } else {
                    setIsFavoriteSeller(true);
                    await followSeller(profileMemberId);
                }
            } catch (error) {
                setIsFavoriteSeller(
                    prevIsFavoriteSeller => !prevIsFavoriteSeller
                );
            }
        } else {
            dispatch(uiActions.signIn(window.location.pathname));
        }
    };

    return (
        <div
            ref={detailsContainerRef}
            className={`${styles.profileDetails} row no-gutters align-items-stretch`}>
            <div className="col-12 col-lg-10 col-xl-8 d-flex flex-column justify-content-center ml-0">
                <div
                    ref={detailsRef}
                    className={`${hasImage ? 'ml-md-2 ml-xl-3' : ''}`}>
                    {hasShop && (
                        <span className="badge badge-dark mb-1">
                            {t('profileHeader_store')}
                        </span>
                    )}
                    <h1 className="heading-madrid text-styled text-break mb-1">
                        {alias}
                    </h1>
                    {detailedSellerRatingAverage && (
                        <div className="d-flex mb-1">
                            <RatingStars
                                memberAlias={alias}
                                memberId={profileMemberId}
                                rating={detailedSellerRatingAverage}
                                dark={true}
                            />
                            <span className="text-dark mx-1">•</span>
                            <ALink
                                href={toLocalizedUrl(
                                    `/profile/feedback/${profileMemberId}/${alias}`
                                )}
                                useLink={true}>
                                {t('profileHeader_feedback_number', {
                                    count: formatNumberWithSeparators(
                                        totalRating
                                    )
                                })}
                            </ALink>
                        </div>
                    )}
                    <p className="text-gray-800">
                        {city} • {t('profileHeader_registered')}{' '}
                        {dayjs(memberSince).format('MMMM YYYY')}
                    </p>
                    {combinedShippingDays > 0 && (
                        <div className="mb-2">
                            <CombinedShippingBadge
                                days={combinedShippingDays}
                            />
                        </div>
                    )}
                    <div className="row pb-2">
                        {profileMemberId !== loggedInMemberId && (
                            <div className="col-6 col-md-5">
                                <Button
                                    theme={`${
                                        isFavoriteSeller ? 'dark' : 'primary'
                                    }`}
                                    fluid
                                    iconFixed
                                    outline={isFavoriteSeller}
                                    className="font-weight-bold text-styled"
                                    onClick={() => handleFollowSeller()}>
                                    <StarIcon className="icon icon-before" />
                                    {isFavoriteSeller
                                        ? t('profileHeader_following')
                                        : t('profileHeader_follow')}
                                </Button>
                            </div>
                        )}
                        <div className="col-6 col-md-auto">
                            <ProfileShareButton profileData={profileData} />
                        </div>
                    </div>
                </div>
                {intro && maxTextHeight ? (
                    <div className={`${hasImage ? 'ml-md-2 ml-xl-3' : ''}`}>
                        <ProfileDescription
                            intro={intro}
                            maxTextHeight={maxTextHeight}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const ProfileDescription = ({ intro, maxTextHeight }) => {
    const { t } = useTranslator();
    const {
        isOverflowing,
        isShowingMore,
        toggleShowMore,
        containerStyle,
        containerRef
    } = useShowMore(maxTextHeight, maxTextHeight);

    return (
        <div className="position-relative">
            <p
                ref={containerRef}
                className={`${styles.profileDescription} text-styled mb-0`}
                style={containerStyle}
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                    __html: intro
                }}
            />
            {isOverflowing ? (
                <div className={`${styles.showMoreButton} bg-white`}>
                    <button
                        className={`btn btn-link px-1`}
                        onClick={toggleShowMore}>
                        {isShowingMore ? (
                            <>
                                {t('profileHeader_intro_showLess')}
                                <ArrowDownIcon className="icon icon-sm ml-1 mirror-y" />
                            </>
                        ) : (
                            <>
                                {t('profileHeader_intro_showMore')}
                                <ArrowDownIcon className="icon icon-sm ml-1" />
                            </>
                        )}
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export { ProfileDetails };
